import { useState } from "react";
import { useNavigate, Link, useSearchParams } from "react-router-dom";
import BrandMark from "../components/BrandMark";
import { SiteFooter } from "../components/Layout";
import { api } from "../lib/api";
import { toast } from "sonner";

export default function ForgotPassword() {
  const [params] = useSearchParams();
  const initial = params.get("mode") === "username" ? "username" : "password";
  const [mode, setMode] = useState(initial);
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      if (mode === "password") {
        await api.post("/auth/forgot-password", { email });
      } else {
        await api.post("/auth/recover-username", { email });
      }
      toast.success(`If an account exists for ${email}, we've sent you an email.`);
      setEmail("");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="dark-theme min-h-screen flex flex-col">
      <div className="flex-1 flex">
        <div className="hidden md:block w-1/2 relative">
          <img
            src="https://images.unsplash.com/photo-1653307370781-a5b033bac658?crop=entropy&cs=srgb&fm=jpg&q=85"
            alt=""
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-background/40 to-background" />
          <Link to="/" className="absolute top-8 left-8 flex items-center gap-3" data-testid="forgot-brand">
            <BrandMark size={40} />
            <span className="font-serif text-2xl">cauldron</span>
          </Link>
        </div>
        <div className="w-full md:w-1/2 flex items-center justify-center p-8 py-12">
          <div className="w-full max-w-md space-y-6">
            <Link to="/" className="md:hidden flex items-center gap-3 mb-2" data-testid="forgot-brand-mobile">
              <BrandMark size={36} />
              <span className="font-serif text-2xl">cauldron</span>
            </Link>
            <div>
              <div className="label-mystic mb-2">Recover Account</div>
              <h1 className="font-serif text-5xl" data-testid="forgot-title">
                {mode === "password" ? "Reset Password" : "Find Your Email"}
              </h1>
              <p className="text-foreground/65 text-sm mt-3">
                {mode === "password"
                  ? "Enter your account email and we'll send you a reset link."
                  : "Enter the email you used to register and we'll confirm it for you."}
              </p>
            </div>

            <div className="flex gap-1 border-b border-secondary/15">
              <button
                onClick={() => setMode("password")}
                className={`px-4 py-2 text-xs uppercase tracking-wider ${mode === "password" ? "text-secondary border-b-2 border-secondary" : "text-foreground/60"}`}
                data-testid="forgot-tab-password"
              >
                Reset Password
              </button>
              <button
                onClick={() => setMode("username")}
                className={`px-4 py-2 text-xs uppercase tracking-wider ${mode === "username" ? "text-secondary border-b-2 border-secondary" : "text-foreground/60"}`}
                data-testid="forgot-tab-username"
              >
                Find Email
              </button>
            </div>

            <form onSubmit={submit} className="space-y-4">
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Email"
                className="w-full bg-input border border-secondary/30 p-3 rounded-sm focus:outline-none focus:border-secondary"
                data-testid="forgot-email-input"
                required
              />
              <button
                disabled={busy}
                className="w-full bg-primary text-primary-foreground py-3 rounded-sm uppercase tracking-wider text-xs disabled:opacity-50"
                data-testid="forgot-submit-btn"
              >
                {busy ? "Sending..." : (mode === "password" ? "Send Reset Link" : "Send My Email")}
              </button>
            </form>

            <div className="text-center text-sm text-foreground/60">
              Remembered? <Link to="/auth?mode=login" className="text-secondary hover:text-secondary/80" data-testid="forgot-back-login">Back to sign in</Link>
            </div>
          </div>
        </div>
      </div>
      <SiteFooter withBottomNavOffset={false} />
    </div>
  );
}
